// Music generator using Web Audio API — royalty-free ambient tracks
// Each mood generates a unique ambient soundscape

const MUSIC_LIBRARY = [
  { id: "none", name: "Nessuna musica", mood: null, icon: "🔇", color: "#9CA3AF" },
  { id: "chill", name: "Chill", mood: "chill", icon: "🌊", color: "#06B6D4" },
  { id: "romantic", name: "Romantico", mood: "romantic", icon: "💫", color: "#EC4899" },
  { id: "party", name: "Party", mood: "party", icon: "🎉", color: "#F59E0B" },
  { id: "sunset", name: "Tramonto", mood: "sunset", icon: "🌅", color: "#F97316" },
  { id: "night", name: "Notte", mood: "night", icon: "🌙", color: "#6366F1" },
  { id: "energy", name: "Energia", mood: "energy", icon: "⚡", color: "#EF4444" },
  { id: "dreamy", name: "Sogno", mood: "dreamy", icon: "☁️", color: "#A78BFA" },
];

// Musical scales and chord progressions per mood
const MOOD_CONFIG = {
  chill: {
    bpm: 70,
    scale: [261.63, 293.66, 329.63, 349.23, 392, 440, 493.88], // C major
    chords: [[0, 2, 4], [3, 5, 0], [4, 6, 1], [3, 5, 0]], // I-IV-V-IV
    padType: "sine",
    padVolume: 0.06,
    arpVolume: 0.03,
    hasArp: true,
    filterFreq: 800,
  },
  romantic: {
    bpm: 60,
    scale: [261.63, 293.66, 311.13, 349.23, 392, 415.30, 466.16], // C minor
    chords: [[0, 2, 4], [5, 0, 2], [3, 5, 0], [4, 6, 1]],
    padType: "sine",
    padVolume: 0.07,
    arpVolume: 0.04,
    hasArp: true,
    filterFreq: 600,
  },
  party: {
    bpm: 120,
    scale: [329.63, 369.99, 415.30, 440, 493.88, 554.37, 622.25], // E major
    chords: [[0, 2, 4], [1, 3, 5], [0, 2, 4], [4, 6, 1]],
    padType: "square",
    padVolume: 0.04,
    arpVolume: 0.05,
    hasArp: true,
    filterFreq: 2000,
  },
  sunset: {
    bpm: 65,
    scale: [349.23, 392, 440, 466.16, 523.25, 587.33, 622.25], // F major
    chords: [[0, 2, 4], [3, 5, 0], [1, 3, 5], [0, 2, 4]],
    padType: "sine",
    padVolume: 0.07,
    arpVolume: 0.025,
    hasArp: true,
    filterFreq: 700,
  },
  night: {
    bpm: 55,
    scale: [220, 246.94, 261.63, 293.66, 329.63, 349.23, 392], // A minor low
    chords: [[0, 2, 4], [5, 0, 2], [3, 5, 0], [5, 0, 2]],
    padType: "sine",
    padVolume: 0.06,
    arpVolume: 0.02,
    hasArp: false,
    filterFreq: 400,
  },
  energy: {
    bpm: 130,
    scale: [329.63, 369.99, 415.30, 440, 493.88, 554.37, 622.25],
    chords: [[0, 2, 4], [4, 6, 1], [3, 5, 0], [1, 3, 5]],
    padType: "sawtooth",
    padVolume: 0.035,
    arpVolume: 0.05,
    hasArp: true,
    filterFreq: 3000,
  },
  dreamy: {
    bpm: 58,
    scale: [293.66, 329.63, 369.99, 392, 440, 493.88, 554.37], // D major
    chords: [[0, 2, 4], [1, 3, 5], [5, 0, 2], [3, 5, 0]],
    padType: "sine",
    padVolume: 0.06,
    arpVolume: 0.03,
    hasArp: true,
    filterFreq: 500,
  },
};

class MusicPlayer {
  constructor() {
    this.ctx = null;
    this.nodes = [];
    this.isPlaying = false;
    this.currentMood = null;
    this.loopTimer = null;
  }

  start(moodId) {
    if (!moodId || moodId === "none") return;
    if (this.isPlaying && this.currentMood === moodId) return;
    this.stop();

    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.currentMood = moodId;
      this.isPlaying = true;
      this._playLoop();
    } catch (e) {
      console.warn("MusicPlayer: AudioContext not available");
    }
  }

  _playLoop() {
    if (!this.isPlaying || !this.ctx) return;
    const config = MOOD_CONFIG[this.currentMood];
    if (!config) return;

    const beatDur = 60 / config.bpm;
    const barDur = beatDur * 4;
    const loopDur = barDur * config.chords.length;
    const now = this.ctx.currentTime;

    // Master gain with fade
    const master = this.ctx.createGain();
    master.gain.setValueAtTime(0, now);
    master.gain.linearRampToValueAtTime(1, now + 0.5);
    master.gain.linearRampToValueAtTime(1, now + loopDur - 0.3);
    master.gain.linearRampToValueAtTime(0, now + loopDur);

    // Low-pass filter for warmth
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = config.filterFreq;
    filter.Q.value = 0.7;
    filter.connect(master);
    master.connect(this.ctx.destination);

    // Play chord pads
    config.chords.forEach((chord, chordIdx) => {
      const chordStart = now + chordIdx * barDur;
      chord.forEach((noteIdx) => {
        const freq = config.scale[noteIdx % config.scale.length];
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = config.padType;
        osc.frequency.value = freq;
        // Detune slightly for warmth
        osc.detune.value = (Math.random() - 0.5) * 8;
        osc.connect(gain);
        gain.connect(filter);
        gain.gain.setValueAtTime(0, chordStart);
        gain.gain.linearRampToValueAtTime(config.padVolume, chordStart + 0.3);
        gain.gain.linearRampToValueAtTime(config.padVolume * 0.7, chordStart + barDur - 0.1);
        gain.gain.linearRampToValueAtTime(0, chordStart + barDur);
        osc.start(chordStart);
        osc.stop(chordStart + barDur + 0.1);
        this.nodes.push(osc);

        // Add octave-up pad for richness
        const osc2 = this.ctx.createOscillator();
        const gain2 = this.ctx.createGain();
        osc2.type = "sine";
        osc2.frequency.value = freq * 2;
        osc2.connect(gain2);
        gain2.connect(filter);
        gain2.gain.setValueAtTime(0, chordStart);
        gain2.gain.linearRampToValueAtTime(config.padVolume * 0.3, chordStart + 0.4);
        gain2.gain.linearRampToValueAtTime(0, chordStart + barDur);
        osc2.start(chordStart);
        osc2.stop(chordStart + barDur + 0.1);
        this.nodes.push(osc2);
      });

      // Arpeggio notes
      if (config.hasArp) {
        const arpNotes = [...chord, chord[0]];
        arpNotes.forEach((noteIdx, arpIdx) => {
          const arpStart = chordStart + arpIdx * beatDur;
          const freq = config.scale[noteIdx % config.scale.length] * 2;
          const osc = this.ctx.createOscillator();
          const gain = this.ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = freq;
          osc.connect(gain);
          gain.connect(filter);
          gain.gain.setValueAtTime(0, arpStart);
          gain.gain.linearRampToValueAtTime(config.arpVolume, arpStart + 0.05);
          gain.gain.exponentialRampToValueAtTime(0.001, arpStart + beatDur * 0.8);
          osc.start(arpStart);
          osc.stop(arpStart + beatDur);
          this.nodes.push(osc);
        });
      }
    });

    // Schedule next loop
    this.loopTimer = setTimeout(() => this._playLoop(), loopDur * 1000 - 100);
  }

  stop() {
    this.isPlaying = false;
    this.currentMood = null;
    if (this.loopTimer) {
      clearTimeout(this.loopTimer);
      this.loopTimer = null;
    }
    this.nodes.forEach((node) => {
      try { node.stop(); } catch (e) { /* already stopped */ }
    });
    this.nodes = [];
    if (this.ctx) {
      try { this.ctx.close(); } catch (e) { /* ignore */ }
      this.ctx = null;
    }
  }
}

// Singleton player
let playerInstance = null;
export const getMusicPlayer = () => {
  if (!playerInstance) playerInstance = new MusicPlayer();
  return playerInstance;
};

export { MUSIC_LIBRARY, MOOD_CONFIG };
export default MusicPlayer;
